import { AsYouType, format, parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Adds automatic formatting to all telephone input fields on the page
 * using libphonenumber-js's AsYouType formatting
 * Ensures E.164 format is submitted with forms
 */
const setupPhoneFormatting = () => {
  // Find all telephone input fields
  const phoneInputs = document.querySelectorAll('input[type="tel"]');

  phoneInputs.forEach(input => {
    // Handle initial values
    if (input.value) {
      const formatter = new AsYouType('US');
      const formattedValue = formatter.input(input.value);
      const phoneNumber = new parsePhoneNumberFromString(formattedValue, 'US');
      if(phoneNumber && phoneNumber.isValid()) {
        input.value = phoneNumber.format('NATIONAL');
      } else {
        input.value = formattedValue;
      }
    }

    // Create a hidden input for the E.164 format
    const hiddenInput = createHiddenE164Input(input);
    if (input.value){ updateE164Value(input.value, hiddenInput); }

    // Set up event listeners for formatting
    input.addEventListener('input', (event) => formatPhoneNumber(event, hiddenInput));

    // Make sure the form submits the E.164 value
    setupFormSubmitHandler(input, hiddenInput);
  });
};

/**
 * Creates a hidden input field to store the E.164 format
 * @param {HTMLInputElement} visibleInput - The visible telephone input
 * @returns {HTMLInputElement} The hidden E.164 input element
 */
const createHiddenE164Input = (visibleInput) => {
  const hiddenInput = document.createElement('input');
  hiddenInput.type = 'hidden';
  hiddenInput.name = visibleInput.name;
  hiddenInput.classList.add('e164-phone-input');

  // Change the visible input name to avoid duplicate form submissions
  visibleInput.setAttribute('data-original-name', visibleInput.name);
  visibleInput.name = `${visibleInput.name}_display`;

  // Insert the hidden input right after the visible one
  visibleInput.parentNode.insertBefore(hiddenInput, visibleInput.nextSibling);

  return hiddenInput;
};

/**
 * Updates the E.164 format in the hidden input
 * @param {string} formattedValue - The formatted phone number
 * @param {HTMLInputElement} hiddenInput - The hidden E.164 input
 */
const updateE164Value = (formattedValue, hiddenInput) => {
  try {
    const phoneNumber = parsePhoneNumberFromString(formattedValue, 'US');
    if (phoneNumber && phoneNumber.isValid()) {
      hiddenInput.value = phoneNumber.format('E.164');
    } else {
      // If not valid, store the raw digits as fallback
      hiddenInput.value = formattedValue.replace(/\D/g, '');
    }
  } catch (error) {
    console.warn('Error parsing phone number:', error);
    hiddenInput.value = formattedValue.replace(/\D/g, '');
  }
};

/**
 * Event handler for input events on phone fields
 * @param {Event} event - The input event
 * @param {HTMLInputElement} hiddenInput - The hidden E.164 input
 */
const formatPhoneNumber = (event, hiddenInput) => {
  const input = event.target;
  const cursorPosition = input.selectionStart;
  const previousLength = input.value.length;

  // Create formatter instance for US numbers
  const formatter = new AsYouType('US');

  // Apply formatting
  const formattedNumber = formatter.input(input.value);
  input.value = formattedNumber;

  // Update the E.164 format in the hidden input
  updateE164Value(formattedNumber, hiddenInput);

  // Adjust cursor position after formatting to avoid jumps
  const lengthDifference = input.value.length - previousLength;
  input.setSelectionRange(cursorPosition + lengthDifference, cursorPosition + lengthDifference);
};

/**
 * Sets up form submit handler to ensure E.164 values are submitted
 * @param {HTMLInputElement} visibleInput - The visible telephone input
 * @param {HTMLInputElement} hiddenInput - The hidden E.164 input
 */
const setupFormSubmitHandler = (visibleInput, hiddenInput) => {
  const form = visibleInput.form;
  if (!form) return;

  // Only add the handler once per form
  if (!form.hasAttribute('data-phone-handler-attached')) {
    form.setAttribute('data-phone-handler-attached', 'true');

    form.addEventListener('submit', () => {
      // Make sure all phone fields in this form have their E.164 values updated
      const phoneInputs = form.querySelectorAll('input[type="tel"]');
      phoneInputs.forEach(input => {
        const associatedHiddenInput = input.nextElementSibling;
        if (associatedHiddenInput && associatedHiddenInput.classList.contains('e164-phone-input')) {
          updateE164Value(input.value, associatedHiddenInput);
        }
      });
    });
  }
};

// Export both as named export and default
export { setupPhoneFormatting };
export default setupPhoneFormatting;